import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert, Modal, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function BookBuilder() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState('list'); // 'list', 'chat', 'finalize', 'complete', 'detail'

  // Chat state
  const [projectId, setProjectId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [sending, setSending] = useState(false);
  const [readyToOutline, setReadyToOutline] = useState(false);

  // Finalize state
  const [bookTitle, setBookTitle] = useState('');
  const [bookSubtitle, setBookSubtitle] = useState('');

  // Completed book kit
  const [bookKit, setBookKit] = useState(null);

  // Detail view
  const [detailProject, setDetailProject] = useState(null);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // No IP warning
  const [showNoIPWarning, setShowNoIPWarning] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Try to extract book title/subtitle from conversation
  const extractTitleFromMessages = () => {
    // Look through assistant messages for title patterns
    for (const msg of messages) {
      if (msg.role === 'assistant') {
        // Look for patterns like "Title: X" or "Working Title: X" or quoted titles
        const titleMatch = msg.content.match(/(?:Working )?Title[:\s]+[""]?([^""\n]+)[""]?/i);
        const subtitleMatch = msg.content.match(/Subtitle[:\s]+[""]?([^""\n]+)[""]?/i);

        if (titleMatch) {
          setBookTitle(titleMatch[1].trim());
        }
        if (subtitleMatch) {
          setBookSubtitle(subtitleMatch[1].trim());
        }

        // If we found a title, stop looking
        if (titleMatch) break;
      }
    }
  };

  const handleMoveToFinalize = () => {
    extractTitleFromMessages();
    setView('finalize');
  };

  const loadProjects = async () => {
    try {
      const res = await fetch('/api/authority-engineer/book/list', {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setProjects(data.projects || []);
        }
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const startNewProject = async () => {
    setSending(true);
    try {
      const res = await fetch('/api/authority-engineer/book/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await res.json();

      if (data.success) {
        setProjectId(data.project_id);
        setMessages(data.messages || []);
        setView('chat');
      } else if (data.error === 'no_ip') {
        setShowNoIPWarning(true);
      } else {
        alert(data.error || 'Failed to start book project');
      }
    } catch (err) {
      console.error('Error starting project:', err);
      alert('Error starting book project');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim() || sending) return;

    const message = userInput.trim();
    setUserInput('');
    setSending(true);

    // Optimistically add user message
    setMessages(prev => [...prev, { role: 'user', content: message }]);

    try {
      const res = await fetch('/api/authority-engineer/book/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          message: message
        })
      });

      const data = await res.json();

      if (data.success) {
        setMessages(data.messages);
        setReadyToOutline(data.ready_to_outline);
      } else {
        alert(data.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const handleGenerateOutline = async () => {
    if (!bookTitle.trim()) {
      alert('Please enter a title for your book');
      return;
    }

    setSending(true);
    try {
      const res = await fetch('/api/authority-engineer/book/generate-outline', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          title: bookTitle.trim(),
          subtitle: bookSubtitle.trim()
        })
      });

      const data = await res.json();

      if (data.success) {
        setBookKit(data.book_kit);
        setView('complete');
        loadProjects();
      } else {
        alert(data.error || 'Failed to generate outline');
      }
    } catch (err) {
      console.error('Error generating outline:', err);
      alert('Error generating book outline');
    } finally {
      setSending(false);
    }
  };

  const handleAbandon = async () => {
    if (!window.confirm('Are you sure you want to abandon this book project? All progress will be lost.')) {
      return;
    }

    try {
      await fetch('/api/authority-engineer/book/abandon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ project_id: projectId })
      });
    } catch (err) {
      console.error('Error abandoning:', err);
    }

    setProjectId(null);
    setMessages([]);
    setReadyToOutline(false);
    setBookTitle('');
    setBookSubtitle('');
    setView('list');
    loadProjects();
  };

  const viewProjectDetail = async (id) => {
    try {
      const res = await fetch(`/api/authority-engineer/book/${id}`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.success) {
        setDetailProject(data.project);
        setView('detail');
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const handleExport = async (id) => {
    try {
      const res = await fetch(`/api/authority-engineer/book/${id}/export`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.success) {
        // Convert markdown to HTML
        const markdownToHtml = (md) => {
          // Handle code blocks first
          let result = md.replace(/```([\s\S]*?)```/g, (match, code) => {
            return `<pre style="background: #f5f5f5; padding: 16px; border-radius: 8px; font-family: Consolas, monospace; font-size: 13px; white-space: pre-wrap; word-wrap: break-word; margin: 16px 0;">${code.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
          });

          return result
            .replace(/^### (.*$)/gim, '<h3 style="color: #333; margin-top: 28px; margin-bottom: 12px;">$1</h3>')
            .replace(/^## (.*$)/gim, '<h2 style="color: #222; margin-top: 36px; margin-bottom: 16px; border-bottom: 2px solid #ddd; padding-bottom: 8px;">$1</h2>')
            .replace(/^# (.*$)/gim, '<h1 style="color: #111; margin-bottom: 20px;">$1</h1>')
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/^- (.*$)/gim, '<li style="margin-left: 24px; margin-bottom: 4px;">$1</li>')
            .replace(/^(\d+)\. (.*$)/gim, '<li style="margin-left: 24px; margin-bottom: 4px;"><strong>$1.</strong> $2</li>')
            .replace(/---/g, '<hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">')
            .replace(/\n\n/g, '</p><p style="margin-bottom: 14px; line-height: 1.7;">')
            .replace(/\n/g, '<br/>');
        };

        const htmlDocument = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${data.title || 'Book Development Kit'}</title>
  <style>
    @media print {
      pre { page-break-inside: avoid; }
      h2, h3 { page-break-after: avoid; }
    }
  </style>
</head>
<body style="font-family: Georgia, 'Times New Roman', serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #333; line-height: 1.6;">
  <div style="text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid ${colors.goldLight};">
    <h1 style="color: ${colors.greyDarkest}; margin-bottom: 8px; font-size: 28px;">${data.title || 'Book Development Kit'}</h1>
    ${data.subtitle ? `<p style="font-size: 18px; color: #666; font-style: italic; margin-top: 8px;">${data.subtitle}</p>` : ''}
  </div>
  <div>
    <p style="margin-bottom: 14px; line-height: 1.7;">
      ${markdownToHtml(data.markdown)}
    </p>
  </div>
</body>
</html>`;

        // Download as HTML file (opens in browser/Word)
        const blob = new Blob([htmlDocument], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename.replace('.md', '.html');
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert(data.error || 'Failed to export');
      }
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Error exporting book kit');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await fetch(`/api/authority-engineer/book/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setProjects(prev => prev.filter(p => p.id !== deleteId));
        if (detailProject?.id === deleteId) {
          setView('list');
          setDetailProject(null);
        }
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'planning': { bg: 'warning', label: 'Planning' },
      'outlined': { bg: 'info', label: 'Outlined' },
      'in_progress': { bg: 'primary', label: 'Writing' },
      'complete': { bg: 'success', label: 'Complete' }
    };
    const v = variants[status] || { bg: 'secondary', label: status };
    return <Badge bg={v.bg} text={v.bg === 'warning' ? 'dark' : undefined}>{v.label}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading Book Builder...</p>
      </Container>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && detailProject) {
    const chapters = detailProject.chapter_outline || [];
    const bookKitData = detailProject.book_kit || {};

    return (
      <Container fluid className="py-4 px-4">
        <Button variant="link" className="mb-3 ps-0" onClick={() => setView('list')}>
          &larr; Back to Books
        </Button>

        <Card className="shadow-sm">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">{detailProject.title || 'Untitled Book'}</h4>
                {detailProject.subtitle && (
                  <small className="text-white-50">{detailProject.subtitle}</small>
                )}
              </div>
              {getStatusBadge(detailProject.status)}
            </div>
          </Card.Header>
          <Card.Body>
            {bookKitData.core_promise && (
              <Alert variant="info" className="mb-4">
                <strong>Core Promise:</strong> {bookKitData.core_promise}
              </Alert>
            )}

            {/* Personal Elements */}
            <h5 className="mb-3">Your Personal "Why"</h5>
            <Card className="mb-4 bg-light">
              <Card.Body>
                {detailProject.personal_why && (
                  <div className="mb-3">
                    <strong>Why You're Writing This:</strong>
                    <p className="mb-0">{detailProject.personal_why}</p>
                  </div>
                )}
                {detailProject.personal_meaning && (
                  <div className="mb-3">
                    <strong>What It Means to You:</strong>
                    <p className="mb-0">{detailProject.personal_meaning}</p>
                  </div>
                )}
                {detailProject.personal_stories?.length > 0 && (
                  <div>
                    <strong>Your Stories:</strong>
                    <ul className="mb-0">
                      {detailProject.personal_stories.map((story, i) => (
                        <li key={i}>{story}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Chapter Outline */}
            <h5 className="mb-3">Chapter Outline ({chapters.length} chapters)</h5>
            {chapters.map((ch, idx) => (
              <Card key={idx} className="mb-3">
                <Card.Body>
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      <Badge bg="dark" style={{ fontSize: '1rem', padding: '0.5em 0.75em' }}>
                        {ch.number || idx + 1}
                      </Badge>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-2">{ch.title}</h6>
                      <p className="text-muted mb-2">{ch.purpose}</p>
                      {ch.key_points?.length > 0 && (
                        <div className="mb-2">
                          <strong className="small">Key Points:</strong>
                          <ul className="small mb-0">
                            {ch.key_points.map((point, i) => (
                              <li key={i}>{point}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {ch.ip_used && (
                        <p className="small text-primary mb-1">
                          <strong>IP Used:</strong> {ch.ip_used}
                        </p>
                      )}
                      {ch.personal_element && (
                        <p className="small text-success mb-0">
                          <strong>Personal Element:</strong> {ch.personal_element}
                        </p>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}

            {/* Actions */}
            <div className="d-flex gap-2 mt-4 flex-wrap">
              <Button variant="primary" onClick={() => handleExport(detailProject.id)}>
                Download Book Kit
              </Button>
              <Button
                variant="outline-warning"
                onClick={() => navigate('/command-center/authority-engineer/book-launch')}
              >
                Launch Checklist
              </Button>
              <Button
                variant="outline-info"
                onClick={() => navigate('/command-center/authority-engineer/amazon-publishing')}
              >
                Publishing Guide
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => {
                  setDeleteId(detailProject.id);
                  setShowDeleteModal(true);
                }}
              >
                Delete Project
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete Book Project</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this book project? This cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    );
  }

  // COMPLETE VIEW
  if (view === 'complete' && bookKit) {
    const chapters = bookKit.chapters || [];

    return (
      <Container fluid className="py-4 px-4">
        <Card className="shadow">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h4 className="mb-0">Your Book Development Kit is Ready!</h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="success" className="mb-4">
              Your book "{bookTitle}" has been outlined with {chapters.length} chapters, personal elements,
              and chapter-by-chapter writing prompts.
            </Alert>

            <h5 className="mb-3">{bookTitle}</h5>
            {bookSubtitle && <h6 className="text-muted">{bookSubtitle}</h6>}
            <p className="mt-3">{bookKit.core_promise}</p>

            <h6 className="mt-4 mb-3">Chapters:</h6>
            <ListGroup className="mb-4">
              {chapters.slice(0, 5).map((ch, idx) => (
                <ListGroup.Item key={idx}>
                  <strong>Chapter {ch.number}:</strong> {ch.title}
                </ListGroup.Item>
              ))}
              {chapters.length > 5 && (
                <ListGroup.Item className="text-muted">
                  ...and {chapters.length - 5} more chapters
                </ListGroup.Item>
              )}
            </ListGroup>

            <h6 className="mt-4 mb-3">What's Included in Your Book Kit:</h6>
            <ul>
              <li>Personal "Why" and meaning captured from your conversation</li>
              <li>Complete chapter outline with key points</li>
              <li>Chapter-by-chapter writing prompts for AI assistance</li>
              <li>Master prompt for writing the entire book with Claude</li>
              <li>Your IP frameworks mapped to each chapter</li>
            </ul>

            <div className="d-flex gap-2 flex-wrap mt-4">
              <Button
                variant="primary"
                onClick={() => handleExport(projectId)}
              >
                Download Book Kit
              </Button>
              <Button
                variant="success"
                onClick={() => navigate(`/command-center/authority-engineer/book/${projectId}/enhance`)}
              >
                Enhance with Research
              </Button>
              <Button
                variant="outline-warning"
                onClick={() => navigate('/command-center/authority-engineer/book-launch')}
              >
                Launch Checklist
              </Button>
              <Button
                variant="outline-info"
                onClick={() => navigate('/command-center/authority-engineer/amazon-publishing')}
              >
                Publishing Guide
              </Button>
              <Button variant="outline-secondary" onClick={() => {
                setBookKit(null);
                setView('list');
                loadProjects();
              }}>
                Back to List
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // FINALIZE VIEW
  if (view === 'finalize') {
    return (
      <Container fluid className="py-4 px-4">
        <Card className="shadow">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h4 className="mb-0">Finalize Your Book</h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="info" className="mb-4">
              Your planning conversation is complete! Now give your book a working title and we'll generate
              your Book Development Kit with chapter outlines and writing prompts.
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label><strong>Book Title</strong></Form.Label>
              <Form.Control
                type="text"
                value={bookTitle}
                onChange={(e) => setBookTitle(e.target.value)}
                placeholder="e.g., The Expert's Edge"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label><strong>Subtitle</strong> (optional)</Form.Label>
              <Form.Control
                type="text"
                value={bookSubtitle}
                onChange={(e) => setBookSubtitle(e.target.value)}
                placeholder="e.g., How to Turn Your Expertise Into a Thriving Business"
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button
                variant="dark"
                onClick={handleGenerateOutline}
                disabled={sending || !bookTitle.trim()}
                style={{ backgroundColor: colors.greyDarkest }}
              >
                {sending ? <><Spinner size="sm" className="me-2" /> Generating...</> : 'Generate Book Kit'}
              </Button>
              <Button variant="outline-secondary" onClick={() => setView('chat')}>
                Back to Conversation
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // CHAT VIEW
  if (view === 'chat') {
    return (
      <Container fluid className="py-4 px-4">
        <Card className="shadow" style={{ height: 'calc(100vh - 150px)' }}>
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Planning Your Book</h5>
                <small>Let's explore your book direction and capture what makes it uniquely yours</small>
              </div>
              <Button variant="outline-light" size="sm" onClick={handleAbandon}>
                Abandon
              </Button>
            </div>
          </Card.Header>

          <Card.Body style={{ overflowY: 'auto', flex: 1, backgroundColor: '#f8f9fa' }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`mb-3 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    backgroundColor: msg.role === 'user' ? colors.greyDarkest : 'white',
                    color: msg.role === 'user' ? 'white' : 'inherit',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {msg.content.replace('[PLANNING_COMPLETE]', '').trim()}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </Card.Body>

          {/* Ready to outline or continue chatting */}
          {readyToOutline ? (
            <div className="p-3 border-top bg-light">
              <Alert variant="success" className="mb-3">
                Planning complete! Ready to generate your Book Development Kit.
              </Alert>
              <div className="d-flex gap-2">
                <Button
                  variant="dark"
                  onClick={handleMoveToFinalize}
                  style={{ backgroundColor: colors.greyDarkest }}
                >
                  Continue to Book Kit
                </Button>
                <Button variant="outline-secondary" onClick={() => setReadyToOutline(false)}>
                  Keep Exploring
                </Button>
              </div>
            </div>
          ) : (
            <Form onSubmit={handleSendMessage} className="p-3 border-top">
              <div className="d-flex gap-2 align-items-end">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (userInput.trim() && !sending) {
                        handleSendMessage(e);
                      }
                    }
                  }}
                  placeholder="Share your thoughts... (Shift+Enter for new line)"
                  disabled={sending}
                  style={{ resize: 'vertical', minHeight: '80px' }}
                />
                <Button
                  type="submit"
                  variant="dark"
                  disabled={sending || !userInput.trim()}
                  style={{ backgroundColor: colors.greyDarkest, height: 'fit-content' }}
                >
                  {sending ? <Spinner size="sm" /> : 'Send'}
                </Button>
              </div>
            </Form>
          )}
        </Card>
      </Container>
    );
  }

  // LIST VIEW (default)
  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Book Builder</h1>
          <p className="text-muted mb-0">
            Turn your intellectual property into a book that establishes your authority.
          </p>
        </div>
        <Button
          variant="dark"
          onClick={startNewProject}
          disabled={sending}
          style={{ backgroundColor: colors.greyDarkest }}
        >
          {sending ? <Spinner size="sm" /> : '+ New Book'}
        </Button>
      </div>

      {/* No IP Warning Modal */}
      <Modal show={showNoIPWarning} onHide={() => setShowNoIPWarning(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Extract Your IP First</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            The Book Builder works best when you have documented intellectual property to build from.
          </p>
          <p className="mb-0">
            Please complete <strong>Module 2: Mind to Method</strong> first to extract your IP,
            then return here to plan your book.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoIPWarning(false)}>
            Close
          </Button>
          <Button
            variant="dark"
            onClick={() => navigate('/command-center/authority-engineer/extract')}
            style={{ backgroundColor: colors.greyDarkest }}
          >
            Extract IP Now
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Empty State */}
      {projects.length === 0 ? (
        <Card className="shadow-sm text-center py-5">
          <Card.Body>
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>&#128214;</div>
            <h4 className="mt-3">No Book Projects Yet</h4>
            <p className="text-muted mb-4">
              Start planning your authority-building book based on your documented IP.
            </p>
            <Button
              variant="dark"
              onClick={startNewProject}
              disabled={sending}
              style={{ backgroundColor: colors.greyDarkest }}
            >
              {sending ? <Spinner size="sm" /> : 'Plan Your First Book'}
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {projects.map(project => (
            <Col lg={6} key={project.id} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="mb-0">{project.title || 'Untitled Book'}</h5>
                    {getStatusBadge(project.status)}
                  </div>

                  {project.subtitle && (
                    <p className="text-muted small mb-2">{project.subtitle}</p>
                  )}

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() => viewProjectDetail(project.id)}
                    >
                      View
                    </Button>
                    {project.status === 'outlined' && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleExport(project.id)}
                      >
                        Download Kit
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setDeleteId(project.id);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Publishing Guides */}
      <Row className="mt-4">
        <Col md={6} className="mb-3">
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <span style={{ fontSize: '2rem', marginRight: '0.75rem' }}>&#128640;</span>
                <h5 className="mb-0">Book Launch Checklist</h5>
              </div>
              <p className="text-muted small flex-grow-1">
                Step-by-step guide to launching your book successfully. Pre-launch prep,
                launch week activities, and post-launch momentum.
              </p>
              <Button
                variant="dark"
                onClick={() => navigate('/command-center/authority-engineer/book-launch')}
                style={{ backgroundColor: colors.greyDarkest }}
              >
                Open Launch Checklist
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <span style={{ fontSize: '2rem', marginRight: '0.75rem' }}>&#128218;</span>
                <h5 className="mb-0">Amazon Publishing Guide</h5>
              </div>
              <p className="text-muted small flex-grow-1">
                Complete walkthrough for publishing on Amazon KDP. Document setup, ISBNs,
                cover requirements, pricing, and submission.
              </p>
              <Button
                variant="dark"
                onClick={() => navigate('/command-center/authority-engineer/amazon-publishing')}
                style={{ backgroundColor: colors.greyDarkest }}
              >
                Open Publishing Guide
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Why Write a Book - smaller info note */}
      <Alert variant="light" className="mt-2 border" style={{ backgroundColor: '#fafafa' }}>
        <small className="text-muted">
          <strong>Why write a book?</strong> A book is the ultimate authority asset — it positions you as the definitive expert,
          opens speaking opportunities, generates leads, and creates a legacy that outlasts any social media post.
        </small>
      </Alert>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Book Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this book project? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default BookBuilder;
